import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    getHostelById,
    createHostel,
    updateHostel,
} from "../../services/hostelService";

const EMPTY_FORM = {
    name: "",
    type: "Boys",
    warden: { name: "", contact: "" },
    address: "",
    isActive: true,
};

const HOSTEL_TYPES = ["Boys", "Girls", "Co-ed"];

function Field({ label, name, value, onChange, error, required = false, placeholder = "" }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            <input
                className={`form-control ${error ? "error" : ""}`}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function HostelForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [fetching, setFetching] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await getHostelById(id);
                const { name, type, warden, address, isActive } = res.data;
                setForm({
                    name: name || "",
                    type: type || "Boys",
                    warden: warden || { name: "", contact: "" },
                    address: address || "",
                    isActive: isActive ?? true,
                });
            } catch (err) {
                toast.error(err.response?.data?.message || "Failed to load hostel");
                navigate("/hostels");
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "wardenName" || name === "wardenContact") {
            setForm((prev) => ({
                ...prev,
                warden: {
                    ...prev.warden,
                    [name === "wardenName" ? "name" : "contact"]: value,
                },
            }));
        } else {
            setForm((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            }));
        }
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.name.trim()) nextErrors.name = "Hostel name is required";
        if (!form.type) nextErrors.type = "Hostel type is required";
        if (form.warden.contact && !/^[0-9+\-\s()]{7,15}$/.test(form.warden.contact)) {
            nextErrors.wardenContact = "Enter a valid contact number";
        }
        return nextErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error("Please fix the errors before saving.");
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await updateHostel(id, form);
                toast.success("Hostel updated successfully");
            } else {
                await createHostel(form);
                toast.success("Hostel created successfully");
            }
            navigate("/hostels");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save hostel");
        } finally {
            setSaving(false);
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading hostel...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Hostel" : "Add Hostel"}</h1>
                    <p className="page-subtitle">Maintain hostel profile, warden contact, and active status.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <Field
                                label="Hostel Name"
                                name="name"
                                required
                                value={form.name}
                                onChange={handleChange}
                                error={errors.name}
                                placeholder="e.g. Boys Hostel A"
                            />

                            <div className="form-group">
                                <label className="form-label">
                                    Type<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className={`form-control ${errors.type ? "error" : ""}`}
                                    name="type"
                                    value={form.type}
                                    onChange={handleChange}
                                >
                                    {HOSTEL_TYPES.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                                {errors.type && <span className="form-error">{errors.type}</span>}
                            </div>

                            <Field
                                label="Warden Name"
                                name="wardenName"
                                value={form.warden.name}
                                onChange={handleChange}
                                placeholder="Optional"
                            />
                            <Field
                                label="Warden Contact"
                                name="wardenContact"
                                value={form.warden.contact}
                                onChange={handleChange}
                                error={errors.wardenContact}
                                placeholder="Optional phone number"
                            />

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Address</label>
                                <textarea
                                    className="form-control"
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Hostel address"
                                    style={{ resize: "vertical" }}
                                />
                            </div>

                            {isEdit && (
                                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                    <label className="form-label">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={form.isActive}
                                            onChange={handleChange}
                                            style={{ marginRight: 8 }}
                                        />
                                        Active
                                    </label>
                                </div>
                            )}
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button type="button" className="btn btn-outline" onClick={() => navigate("/hostels")}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? "Saving..." : isEdit ? "Update Hostel" : "Add Hostel"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
